import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import {
  Building2, Users2, TicketCheck, Plus, Eye, Edit2, Trash2, X,
  CheckCircle, Search, DollarSign, Mail, RefreshCw, Users,
  CalendarClock, FileText, Upload
} from "lucide-react";
import { apiBaseUrl } from '../../services/config';
import CompanyMigrationModal from "./CompanyMigrationModal";
const API = apiBaseUrl;
const ax = () => ({ headers: { Authorization: `Bearer ${
  sessionStorage.getItem("super_token") ||
  JSON.parse(sessionStorage.getItem("platform_user")||"{}").access_token ||
  sessionStorage.getItem("access_token") || ""
}` } });

// ── Shared styles ─────────────────────────────────────────────────
const card = { background:"#0D1B2E", border:"1px solid rgba(0,200,255,0.1)", borderRadius:12, overflow:"hidden" };
const inp  = (err=false) => ({
  width:"100%", background:"rgba(255,255,255,0.04)",
  border:`1px solid ${err?"#FF4D6D":"rgba(0,200,255,0.1)"}`,
  borderRadius:8, color:"#E8F0FE", fontSize:13, padding:"9px 12px",
  outline:"none", fontFamily:"Outfit,sans-serif", boxSizing:"border-box",
});
const lbl = { fontSize:11, color:"#7A93B4", display:"block", marginBottom:4,
  textTransform:"uppercase", letterSpacing:"0.07em" };
const P_COLOR = { High:"#FF4D6D", Medium:"#FFB800", Low:"#00E5A3" };
const S_COLOR = { Open:"#FFB800","In Progress":"#00C8FF",Resolved:"#00E5A3",Closed:"#7A93B4" };

// Company status colors & labels
const STATUS_COLOR = {
  Active:    "#00E5A3",
  Trial:     "#00C8FF",
  Inactive:  "#FFB800",
  Suspended: "#FF9500",
  Deleted:   "#FF4D6D",
};
const STATUS_OPTIONS = ["Active","Trial","Inactive","Suspended","Deleted"];

// ── 4 Plans — matches HIRALITYGreen.jsx exactly ───────────────────
const PLANS = ["Basic HRMS","Advanced HRMS","AI Interview","Advanced HRMS + AI","Enterprise"];

const PLAN_CONFIG = {
  "Basic HRMS":           { base:4500, perEmp:45,  perInt:0,  color:"#2563EB", label:"Basic HRMS",           desc:"\u20b94,500/mo + \u20b945/emp" },
  "Advanced HRMS":        { base:8500, perEmp:99,  perInt:0,  color:"#0E7490", label:"Advanced HRMS",        desc:"\u20b95,500/mo + \u20b999/emp" },
  "AI Interview":         { base:3500, perEmp:0,   perInt:65, color:"#059669", label:"AI Interview",         desc:"\u20b93,500/mo + \u20b965/int" },
  "Advanced HRMS + AI":   { base:9999, perEmp:99,  perInt:65,  color:"#2563EB", label:"Advanced HRMS + AI",   desc:"\u20b99,999/mo + \u20b999/emp" },
  "Enterprise":           { base:null, perEmp:0,   perInt:0,  color:"#FFB800", label:"Enterprise",           desc:"Custom pricing" },
  // Legacy support
  "HRMS + AI Bundle":     { base:7000, perEmp:65,  perInt:0,  color:"#00E5A3", label:"HRMS + AI Bundle",     desc:"\u20b97,000/mo + \u20b965/emp" },
  "Basic":                { base:4500, perEmp:45,  perInt:0,  color:"#2563EB", label:"Basic",                desc:"\u20b94,500/mo + \u20b945/emp" },
};

const fmtRs = (n) => n == null ? "Custom" : `\u20b9${Math.round(n).toLocaleString("en-IN")}`;

const calcBill = (plan, empCount, intCount) => {
  const p = PLAN_CONFIG[plan] || PLAN_CONFIG["Basic HRMS"];
  if (p.base === null) return null;
  return p.base + (empCount * p.perEmp) + (intCount * p.perInt);
};

const lastMonthLabel = () => {
  const d = new Date(); d.setMonth(d.getMonth()-1);
  return d.toLocaleString("en-IN",{month:"long",year:"numeric"});
};

// ── Invoice generator ─────────────────────────────────────────────
function generateInvoice(company, empCount, intCount, plan) {
  const p      = PLAN_CONFIG[plan] || PLAN_CONFIG["Basic HRMS"];
  const month  = lastMonthLabel();
  const invNo  = `INV-${company.id}-${new Date().getFullYear()}${String(new Date().getMonth()+1).padStart(2,"0")}`;
  const baseA  = p.base || 0;
  const empA   = (p.perEmp||0) * empCount;
  const intA   = (p.perInt||0) * intCount;
  const sub    = baseA + empA + intA;
  const gst    = Math.round(sub * 0.18);
  const total  = sub + gst;

  const lines = [
    { d:`${plan} — Base Plan`,                         q:"1 month",     a:fmtRs(baseA) },
    p.perEmp&&empCount>0 ? { d:`Per Employee (${empCount} \u00d7 \u20b9${p.perEmp})`, q:`${empCount} emp`, a:fmtRs(empA) } : null,
    p.perInt&&intCount>0 ? { d:`AI Interview (${intCount} \u00d7 \u20b9${p.perInt})`, q:`${intCount} int`, a:fmtRs(intA) } : null,
  ].filter(Boolean);

  const w = window.open("","_blank");
  w.document.write(`<!DOCTYPE html><html><head>
  <title>Invoice \u2014 ${company.name} \u2014 ${month}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0;}
    body{font-family:'Segoe UI',Arial,sans-serif;color:#1E293B;padding:40px;}
    .wrap{max-width:720px;margin:0 auto;}
    .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:36px;padding-bottom:22px;border-bottom:3px solid #2563EB;}
    .logo{font-size:24px;font-weight:900;color:#2563EB;}
    .sub{font-size:9px;color:#94A3B8;letter-spacing:.15em;text-transform:uppercase;}
    h2{font-size:26px;font-weight:900;text-align:right;}
    .meta{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-bottom:28px;}
    .meta h4{font-size:10px;color:#94A3B8;text-transform:uppercase;letter-spacing:.08em;margin-bottom:8px;}
    .meta p{font-size:13px;color:#1E293B;font-weight:600;line-height:1.7;}
    table{width:100%;border-collapse:collapse;margin-bottom:20px;}
    th{font-size:11px;color:#64748B;text-transform:uppercase;letter-spacing:.07em;padding:10px 14px;background:#F8FAFC;border-bottom:1px solid #E2E8F0;text-align:left;}
    td{padding:11px 14px;border-bottom:1px solid #F1F5F9;font-size:13.5px;}
    .tr td{text-align:right;color:#64748B;padding:8px 14px;}
    .tot td{font-weight:900;font-size:16px;border-top:2px solid #2563EB;padding:13px 14px;}
    .tot td:last-child{color:#2563EB;font-size:20px;text-align:right;}
    .footer{margin-top:36px;padding-top:18px;border-top:1px solid #E2E8F0;font-size:11px;color:#94A3B8;text-align:center;line-height:2;}
  </style></head><body><div class="wrap">
  <div class="header">
    <div><div class="logo">HIRALITY</div><div class="sub">A Hire Reality</div>
      <p style="font-size:12px;color:#64748B;margin-top:10px;line-height:1.7">
        Hirality Technologies Pvt. Ltd.<br>Bangalore, Karnataka<br>support@hirality.com
      </p>
    </div>
    <div style="text-align:right">
      <h2>INVOICE</h2>
      <p style="font-size:13px;color:#64748B;margin-top:6px;line-height:1.8">
        <b>Invoice #:</b> ${invNo}<br>
        <b>Month:</b> ${month}<br>
        <span style="padding:2px 10px;border-radius:100px;background:#FEF3C7;color:#D97706;font-size:11px;font-weight:700">PENDING</span>
      </p>
    </div>
  </div>
  <div class="meta">
    <div><h4>Bill To</h4><p>${company.name}<br>${company.admin_email||""}<br>Plan: ${plan}</p></div>
    <div><h4>Summary</h4><p>Period: ${month}<br>Employees: ${empCount}${intCount>0?`<br>AI Interviews: ${intCount}`:""}</p></div>
  </div>
  <table><thead><tr><th>Description</th><th>Qty</th><th style="text-align:right">Amount</th></tr></thead><tbody>
    ${lines.map(l=>`<tr><td>${l.d}</td><td style="color:#64748B">${l.q}</td><td style="text-align:right;font-weight:600">${l.a}</td></tr>`).join("")}
    <tr class="tr"><td colspan="2">Subtotal</td><td>${fmtRs(sub)}</td></tr>
    <tr class="tr"><td colspan="2">GST @ 18%</td><td>${fmtRs(gst)}</td></tr>
    <tr class="tot"><td colspan="2">Total Due</td><td>${fmtRs(total)}</td></tr>
  </tbody></table>
  <div class="footer">Due within 7 days &nbsp;\u00b7&nbsp; NEFT/UPI: support@hirality.com &nbsp;\u00b7&nbsp; GSTIN: 29XXXXX0000X1ZX</div>
  </div></body></html>`);
  w.document.close(); w.print();
}

export default function CompaniesDashboard() {
  const location = useLocation();
  const path = location.pathname;
  const page = path.includes("/tickets")   ? "tickets"
             : path.includes("/billing")   ? "billing"
             : path.includes("/settings")  ? "settings"
             : path.includes("/analytics") ? "analytics"
             : "companies";

  const [companies,    setCompanies]    = useState([]);
  const [billingStats, setBillingStats] = useState([]);
  const [tickets,      setTickets]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [billLoading,  setBillLoading]  = useState(false);
  const [toast,        setToast]        = useState("");
  const [search,       setSearch]       = useState("");

  // Per-company plan overrides — updates instantly in UI before API responds
  const [planOverrides, setPlanOverrides] = useState({});
  const getLivePlan = (c) => planOverrides[c.id] ?? c.plan ?? "Basic HRMS";

  // Modals
  const [viewCo,   setViewCo]   = useState(null);
  const [editCo,   setEditCo]   = useState(null);
  const [createCo, setCreateCo] = useState(false);
  const [coForm,   setCoForm]   = useState({ name:"",domain:"",admin_email:"",admin_name:"",plan:"Basic HRMS",max_employees:50,notes:"",modules:"hrms" });
  const [delCo,    setDelCo]    = useState(null);
  const [purgeCo,  setPurgeCo]  = useState(null);
  const [migrateCo, setMigrateCo] = useState(null);   // company currently targeted for data migration
  const [viewTicket,   setViewTicket]   = useState(null);
  const [replyText,    setReplyText]    = useState("");
  const [ticketFilter, setTicketFilter] = useState("all");

  const flash = (msg) => { setToast(msg); setTimeout(()=>setToast(""),3500); };

  const load = async () => {
    setLoading(true);
    try {
      const [cRes, tRes] = await Promise.all([
        axios.get(`${API}/companies/`, ax()),
        axios.get(`${API}/tickets/`,   ax()),
      ]);
      setCompanies(cRes.data || []);
      setTickets(tRes.data   || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadBillingStats = async () => {
    setBillLoading(true);
    try {
      const res = await axios.get(`${API}/companies/billing-stats`, ax());
      setBillingStats(res.data || []);
    } catch {
      setBillingStats(companies.map(c=>({
        id:c.id, plan:c.plan||"Basic HRMS", status:c.status||"Active",
        employee_count:c.employee_count||0, interviews_last_month:0,
        billing_month:lastMonthLabel(),
      })));
    }
    finally { setBillLoading(false); }
  };

  useEffect(()=>{ load(); },[]);
  useEffect(()=>{ if(page==="billing"||page==="companies") loadBillingStats(); },[page,companies.length]);

  // Merge companies + billing stats
  const rows = companies.map(c=>{
    const bs   = billingStats.find(b=>b.id===c.id);
    const plan = getLivePlan(c);
    const emp  = c.employee_count || 0;
    const int_ = bs?.interviews_last_month || 0;
    return { ...c, plan, interviews_last_month:int_, bill:calcBill(plan,emp,int_), billing_month:bs?.billing_month||lastMonthLabel() };
  });

  const totalRevenue = rows.filter(c=>c.status==="Active"&&c.bill!=null).reduce((a,c)=>a+(c.bill||0),0);

  const handlePlanChange = async (co, newPlan) => {
    // Instant UI update
    setPlanOverrides(p=>({...p,[co.id]:newPlan}));
    try {
      await axios.put(`${API}/companies/${co.id}`, {...co, plan:newPlan}, ax());
      flash(`✅ Plan updated → ${newPlan}`);
      load();
    } catch { flash("❌ Error updating plan"); }
  };

  const createCompany = async () => {
    if(!coForm.name||!coForm.admin_email) return;
    try {
      const { modules, ...companyPayload } = coForm;
      const res = await axios.post(`${API}/companies/`, {
        ...companyPayload,
        email: coForm.admin_email,   // required by CompanyCreate schema
        hrms_enabled: modules === "hrms" || modules === "both",
        ats_enabled:  modules === "ats"  || modules === "both",
      }, ax());

      setCreateCo(false);
      setCoForm({name:"",domain:"",admin_email:"",admin_name:"",plan:"Basic HRMS",max_employees:50,notes:"",modules:"hrms"});
      load();
      flash(`✅ Company created (${modules==="both"?"HRMS + ATS":modules==="ats"?"ATS only":"HRMS only"}) — invite sent`);
    } catch(e){ flash("❌ "+(e.response?.data?.detail||"Error")); }
  };

  const updateCompany = async () => {
    try {
      await axios.put(`${API}/companies/${editCo.id}`,editCo,ax());
      setEditCo(null); load(); flash("✅ Company updated");
    } catch(e){ flash("❌ "+(e.response?.data?.detail||"Error")); }
  };

  const handleStatusChange = async (co, newStatus) => {
    if (newStatus === co.status) return;
    try {
      const params = new URLSearchParams(); params.append("status", newStatus);
      await axios.put(`${API}/companies/${co.id}/status?${params.toString()}`, {}, ax());
      flash(`✅ ${co.name} → ${newStatus}`);
      load();
    } catch(e) { flash("❌ "+(e.response?.data?.detail||"Error")); }
  };

  const deleteCompany = async (id) => {
    // Soft delete — marks as Deleted, deactivates users, preserves data
    try {
      await axios.delete(`${API}/companies/${id}`, ax());
      setDelCo(null); load(); flash("✅ Company marked as Deleted — data preserved");
    } catch(e) { flash("❌ "+(e.response?.data?.detail||"Error")); }
  };

  const permanentDelete = async (id) => {
    // Hard delete — only allowed when status is already Deleted
    try {
      await axios.delete(`${API}/companies/${id}/permanent`, ax());
      setPurgeCo(null); load(); flash("✅ Company permanently deleted");
    } catch(e) { flash("❌ "+(e.response?.data?.detail||"Error")); }
  };

  const resendInvite = async (id) => {
    try {
      const r=await axios.post(`${API}/companies/${id}/resend-invite`,{},ax());
      flash(`✅ Invite resent to ${r.data?.email||"admin"}`);
    } catch(e){ flash("❌ "+(e.response?.data?.detail||"Error")); }
  };

  // ── ATS onboarding ────────────────────────────────────────────
  // Enables the ATS module for an existing HRMS company: syncs the ATS
  // permission catalog, seeds the 5 default ATS roles, and seeds default
  // ticket statuses/priorities for this company. Idempotent — safe to
  // click again on an already-enabled company (returns 0/0 created).
  const enableAtsForCompany = async (co) => {
    try {
      const r = await axios.post(
        `${API}/api/v1/ats/admin/onboard-company`,
        { company_id: co.id },
        ax()
      );
      flash(`✅ ATS enabled for ${co.name} (${r.data.statuses_created} statuses, ${r.data.priorities_created} priorities seeded)`);
    } catch (e) {
      flash("❌ " + (e.response?.data?.detail || "Error enabling ATS"));
    }
  };

  const toggleStatus = async (co) => {
    const s = co.status==="Active"?"Inactive":"Active";
    try {
      await axios.put(`${API}/companies/${co.id}`,{...co,status:s},ax());
      load(); flash(`✅ Company ${s}`);
    } catch { flash("❌ Error"); }
  };

  const updateTicket = async (id,status,resolution="") => {
    try {
      const p=new URLSearchParams(); p.append("status",status);
      if(resolution) p.append("resolution",resolution);
      await axios.put(`${API}/tickets/${id}?${p.toString()}`,{},ax());
      setViewTicket(null); setReplyText(""); load(); flash(`✅ Ticket marked ${status}`);
    } catch(e){ flash("❌ "+(e.response?.data?.detail||"Error")); }
  };

  const filtered = rows.filter(c=>!search||c.name?.toLowerCase().includes(search.toLowerCase())||c.admin_email?.toLowerCase().includes(search.toLowerCase()));
  const filteredTickets = ticketFilter==="all"?tickets:tickets.filter(t=>t.status===ticketFilter);

  const stats = {
    companies:  companies.length,
    active:     companies.filter(c=>c.status==="Active").length,
    openTix:    tickets.filter(t=>t.status==="Open").length,
    revenue:    totalRevenue,
  };

  const TH = ({children,style={}}) => (
    <th style={{padding:"11px 14px",textAlign:"left",fontSize:11,color:"#7A93B4",textTransform:"uppercase",letterSpacing:"0.07em",fontWeight:600,...style}}>{children}</th>
  );

  const PlanBadge = ({plan}) => {
    const cfg = PLAN_CONFIG[plan]||PLAN_CONFIG["Basic HRMS"];
    return <span style={{fontSize:11,padding:"2px 8px",borderRadius:100,fontWeight:700,background:`${cfg.color}18`,color:cfg.color}}>{plan}</span>;
  };

  return (
    <div style={{fontFamily:"Outfit,sans-serif",color:"#E8F0FE"}}>

      {/* ═══════════ COMPANIES ═══════════ */}
      {page==="companies" && (<>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24}}>
          <div>
            <div style={{fontSize:11,color:"#00C8FF",fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:4}}>SUPER ADMIN</div>
            <h2 style={{margin:0,fontSize:22,fontWeight:800}}>Companies</h2>
            <div style={{fontSize:12,color:"#7A93B4",marginTop:3}}>Plan changes update billing instantly</div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={loadBillingStats} style={{display:"flex",alignItems:"center",gap:6,padding:"9px 16px",borderRadius:9,border:"1px solid rgba(0,200,255,0.2)",background:"none",color:"#00C8FF",cursor:"pointer",fontFamily:"Outfit,sans-serif",fontSize:13,fontWeight:600}}>
              <RefreshCw size={13}/> Refresh
            </button>
            <button onClick={()=>{setCoForm({name:"",domain:"",admin_email:"",admin_name:"",plan:"Basic HRMS",max_employees:50,notes:"",modules:"hrms"});setCreateCo(true);}}
              style={{display:"flex",alignItems:"center",gap:7,padding:"10px 20px",borderRadius:9,border:"none",background:"linear-gradient(135deg,#00C8FF,#7C5CFF)",color:"#fff",cursor:"pointer",fontFamily:"Outfit,sans-serif",fontSize:13,fontWeight:700}}>
              <Plus size={14}/> New Company
            </button>
          </div>
        </div>

        {/* Stats */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:20}}>
          {[
            {label:"Total Companies", val:stats.companies, color:"#00C8FF",icon:Building2},
            {label:"Active",          val:stats.active,    color:"#00E5A3",icon:CheckCircle},
            {label:"Monthly Revenue", val:`\u20b9${Math.round(stats.revenue/1000)}K`, color:"#FFB800",icon:DollarSign},
            {label:"Open Tickets",    val:stats.openTix,   color:"#FF4D6D",icon:TicketCheck},
          ].map(({label,val,color,icon:Icon})=>(
            <div key={label} style={{...card,padding:18,borderTop:`3px solid ${color}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                <div style={{fontSize:26,fontWeight:900,fontFamily:"JetBrains Mono,monospace",color}}>{val}</div>
                <Icon size={18} style={{color,opacity:0.5}}/>
              </div>
              <div style={{fontSize:11,color:"#7A93B4",textTransform:"uppercase",letterSpacing:"0.07em",marginTop:6}}>{label}</div>
            </div>
          ))}
        </div>

        {/* Search */}
        <div style={{position:"relative",marginBottom:14}}>
          <Search size={13} style={{position:"absolute",left:12,top:11,color:"#7A93B4"}}/>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search companies\u2026" style={{...inp(),paddingLeft:34}}/>
        </div>

        {/* Table */}
        <div style={{...card,overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13,minWidth:900}}>
            <thead>
              <tr style={{borderBottom:"1px solid rgba(0,200,255,0.08)"}}>
                <TH>Company</TH>
                <TH>Plan</TH>
                <TH style={{textAlign:"center"}}>Employees</TH>
                <TH style={{textAlign:"center"}}>Interviews</TH>
                <TH>Monthly Bill</TH>
                <TH>Status</TH>
                <TH>Actions</TH>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c,i)=>{
                const cfg    = PLAN_CONFIG[c.plan]||PLAN_CONFIG["Basic HRMS"];
                const empCnt = c.employee_count||0;
                const intCnt = c.interviews_last_month||0;
                return (
                  <tr key={c.id} style={{borderBottom:"1px solid rgba(0,200,255,0.04)",background:i%2===0?"transparent":"rgba(255,255,255,0.01)"}}>
                    <td style={{padding:"13px 14px"}}>
                      <div style={{fontWeight:600}}>{c.name}</div>
                      <div style={{fontSize:11,color:"#7A93B4",marginTop:2}}>{c.admin_email}</div>
                    </td>
                    <td style={{padding:"13px 14px"}}>
                      <select value={c.plan} onChange={e=>handlePlanChange(c,e.target.value)}
                        style={{background:"rgba(255,255,255,0.04)",border:`1px solid ${cfg.color}40`,borderRadius:8,
                          color:cfg.color,padding:"6px 10px",fontSize:12,fontFamily:"Outfit,sans-serif",cursor:"pointer",fontWeight:700,display:"block",marginBottom:4}}>
                        {PLANS.map(p=><option key={p} value={p}>{p}</option>)}
                      </select>
                      <div style={{fontSize:10,color:"#3D5473"}}>{cfg.desc}</div>
                    </td>
                    <td style={{padding:"13px 14px",textAlign:"center"}}>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
                        <Users size={12} style={{color:"#00C8FF"}}/>
                        <span style={{fontFamily:"JetBrains Mono,monospace",color:"#00C8FF",fontSize:14,fontWeight:700}}>{empCnt}</span>
                      </div>
                    </td>
                    <td style={{padding:"13px 14px",textAlign:"center"}}>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
                        <CalendarClock size={12} style={{color:"#7C5CFF"}}/>
                        <span style={{fontFamily:"JetBrains Mono,monospace",color:"#7C5CFF",fontSize:14,fontWeight:700}}>{intCnt}</span>
                      </div>
                    </td>
                    <td style={{padding:"13px 14px"}}>
                      <div style={{fontFamily:"JetBrains Mono,monospace",color:"#00E5A3",fontSize:15,fontWeight:800}}>{fmtRs(c.bill)}</div>
                      <div style={{fontSize:10,color:"#3D5473",marginTop:2}}>{cfg.desc}</div>
                    </td>
                    <td style={{padding:"13px 14px"}}>
                      <select
                        value={c.status||"Active"}
                        onChange={e=>handleStatusChange(c,e.target.value)}
                        style={{
                          background:"rgba(255,255,255,0.04)",
                          border:`1px solid ${STATUS_COLOR[c.status||"Active"]}40`,
                          borderRadius:8, color:STATUS_COLOR[c.status||"Active"],
                          padding:"5px 10px", fontSize:12, fontFamily:"Outfit,sans-serif",
                          cursor:"pointer", fontWeight:700,
                        }}>
                        {STATUS_OPTIONS.map(s=><option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td style={{padding:"13px 14px"}}>
                      <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                        <button onClick={()=>setViewCo(c)} style={{padding:"4px 10px",borderRadius:6,border:"1px solid rgba(0,200,255,0.2)",background:"none",color:"#00C8FF",cursor:"pointer",fontSize:12,fontFamily:"Outfit,sans-serif"}}>
                          <Eye size={11} style={{display:"inline",marginRight:3}}/>View
                        </button>
                        <button onClick={()=>setEditCo({...c})} style={{padding:"4px 10px",borderRadius:6,border:"1px solid rgba(124,92,255,0.2)",background:"none",color:"#7C5CFF",cursor:"pointer",fontSize:12,fontFamily:"Outfit,sans-serif"}}>
                          <Edit2 size={11} style={{display:"inline",marginRight:3}}/>Edit
                        </button>
                        <button onClick={()=>setMigrateCo(c)} title="Migrate this company's existing HR data"
                          style={{padding:"4px 10px",borderRadius:6,border:"1px solid rgba(0,229,163,0.3)",background:"rgba(0,229,163,0.08)",color:"#00E5A3",cursor:"pointer",fontSize:12,fontFamily:"Outfit,sans-serif",fontWeight:600}}>
                          <Upload size={11} style={{display:"inline",marginRight:3}}/>Migrate
                        </button>
                        <button onClick={()=>enableAtsForCompany(c)} title="Enable ATS ticketing for this company"
                          style={{padding:"4px 10px",borderRadius:6,border:"1px solid rgba(0,200,255,0.3)",background:"rgba(0,200,255,0.08)",color:"#00C8FF",cursor:"pointer",fontSize:12,fontFamily:"Outfit,sans-serif",fontWeight:600}}>
                          <TicketCheck size={11} style={{display:"inline",marginRight:3}}/>Enable ATS
                        </button>
                        <button onClick={()=>resendInvite(c.id)} style={{padding:"4px 10px",borderRadius:6,border:"1px solid rgba(255,184,0,0.2)",background:"none",color:"#FFB800",cursor:"pointer",fontSize:12,fontFamily:"Outfit,sans-serif"}}>
                          <Mail size={11} style={{display:"inline",marginRight:3}}/>Invite
                        </button>
                        <button onClick={()=>generateInvoice(c,empCnt,intCnt,c.plan)}
                          style={{padding:"4px 10px",borderRadius:6,border:"1px solid rgba(0,229,163,0.2)",background:"rgba(0,229,163,0.06)",color:"#00E5A3",cursor:"pointer",fontSize:12,fontFamily:"Outfit,sans-serif"}}>
                          <FileText size={11} style={{display:"inline",marginRight:3}}/>Invoice
                        </button>
                        {c.status === "Deleted" ? (
                          <button onClick={()=>setPurgeCo(c)} title="Permanently delete all data"
                            style={{padding:"4px 10px",borderRadius:6,border:"1px solid rgba(255,77,109,0.5)",background:"rgba(255,77,109,0.1)",color:"#FF4D6D",cursor:"pointer",fontSize:11,fontFamily:"Outfit,sans-serif",fontWeight:700}}>
                            ☠ Purge
                          </button>
                        ) : (
                          <button onClick={()=>setDelCo(c)} title="Soft delete — preserves data"
                            style={{padding:"4px 8px",borderRadius:6,border:"1px solid rgba(255,77,109,0.2)",background:"none",color:"#FF4D6D",cursor:"pointer"}}>
                            <Trash2 size={11}/>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length===0&&<tr><td colSpan={7} style={{padding:40,textAlign:"center",color:"#7A93B4"}}>No companies yet</td></tr>}
            </tbody>
          </table>
        </div>
      </>)}

      {/* ═══════════ BILLING ═══════════ */}
      {page==="billing" && (<>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24}}>
          <div>
            <div style={{fontSize:11,color:"#00C8FF",fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:4}}>SUPER ADMIN</div>
            <h2 style={{margin:0,fontSize:22,fontWeight:800}}>Billing & Revenue</h2>
            <div style={{fontSize:12,color:"#7A93B4",marginTop:3}}>Auto-generated for {lastMonthLabel()}</div>
          </div>
          <button onClick={loadBillingStats} disabled={billLoading}
            style={{display:"flex",alignItems:"center",gap:6,padding:"9px 18px",borderRadius:9,border:"1px solid rgba(0,200,255,0.2)",background:"none",color:"#00C8FF",cursor:"pointer",fontFamily:"Outfit,sans-serif",fontSize:13,fontWeight:600,opacity:billLoading?0.6:1}}>
            <RefreshCw size={13}/> {billLoading?"Refreshing\u2026":"Refresh"}
          </button>
        </div>

        {/* Plan pricing cards */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:14,marginBottom:24}}>
          {[
            {name:"Basic HRMS",         price:"\u20b94,500", per:"+\u20b945/emp",   color:"#2563EB", count:rows.filter(c=>c.plan==="Basic HRMS").length},
            {name:"Advanced HRMS",      price:"\u20b96,500", per:"+\u20b965/emp",   color:"#0E7490", count:rows.filter(c=>c.plan==="Advanced HRMS").length},
            {name:"AI Interview",       price:"\u20b93,500", per:"+\u20b935/int",   color:"#059669", count:rows.filter(c=>c.plan==="AI Interview").length},
            {name:"Advanced HRMS + AI", price:"\u20b99,000", per:"+\u20b980/emp",   color:"#2563EB", count:rows.filter(c=>c.plan==="Advanced HRMS + AI").length},
          ].map(({name,price,per,color,count})=>(
            <div key={name} style={{...card,padding:20,borderTop:`3px solid ${color}`}}>
              <div style={{fontSize:13,fontWeight:700,color,marginBottom:6}}>{name}</div>
              <div style={{fontSize:22,fontWeight:900,fontFamily:"JetBrains Mono,monospace",color:"#E8F0FE"}}>{price}<span style={{fontSize:13,color:"#7A93B4",fontWeight:400}}>/mo</span></div>
              <div style={{fontSize:12,color:"#7A93B4",margin:"4px 0 14px"}}>{per}</div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:11,color:"#3D5473"}}>Subscribed</span>
                <span style={{fontSize:15,fontWeight:800,fontFamily:"JetBrains Mono,monospace",color}}>{count}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Revenue summary */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:24}}>
          {[
            {label:"Total Monthly Revenue", val:fmtRs(totalRevenue), color:"#00E5A3"},
            {label:"Active Subscriptions",  val:rows.filter(c=>c.status==="Active").length, color:"#00C8FF"},
            {label:"Avg Bill / Company",    val:fmtRs(rows.filter(c=>c.bill).length?totalRevenue/rows.filter(c=>c.bill).length:0), color:"#FFB800"},
          ].map(({label,val,color})=>(
            <div key={label} style={{...card,padding:20}}>
              <div style={{fontSize:11,color:"#7A93B4",textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8}}>{label}</div>
              <div style={{fontSize:26,fontWeight:900,fontFamily:"JetBrains Mono,monospace",color}}>{val}</div>
            </div>
          ))}
        </div>

        {/* Per-company billing table */}
        <div style={{...card,overflowX:"auto"}}>
          <div style={{padding:"14px 18px",borderBottom:"1px solid rgba(0,200,255,0.08)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{fontSize:14,fontWeight:700}}>Per-Company Bill \u2014 {lastMonthLabel()}</div>
            <div style={{fontSize:11,color:"#7A93B4"}}>Change plan \u2192 bill updates instantly</div>
          </div>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13,minWidth:800}}>
            <thead>
              <tr style={{borderBottom:"1px solid rgba(0,200,255,0.08)"}}>
                <TH>Company</TH>
                <TH>Plan</TH>
                <TH style={{textAlign:"center"}}>Employees</TH>
                <TH style={{textAlign:"center"}}>Interviews</TH>
                <TH>Breakdown</TH>
                <TH>Total Bill</TH>
                <TH>Invoice</TH>
              </tr>
            </thead>
            <tbody>
              {rows.map((c,i)=>{
                const cfg    = PLAN_CONFIG[c.plan]||PLAN_CONFIG["Basic HRMS"];
                const empCnt = c.employee_count||0;
                const intCnt = c.interviews_last_month||0;
                const bill   = c.bill;
                return (
                  <tr key={c.id} style={{borderBottom:"1px solid rgba(0,200,255,0.04)",background:i%2===0?"transparent":"rgba(255,255,255,0.01)"}}>
                    <td style={{padding:"12px 14px"}}>
                      <div style={{fontWeight:600}}>{c.name}</div>
                      <div style={{fontSize:11,color:"#7A93B4"}}>{c.admin_email}</div>
                    </td>
                    <td style={{padding:"12px 14px"}}>
                      <select value={c.plan} onChange={e=>handlePlanChange(c,e.target.value)}
                        style={{background:"rgba(255,255,255,0.04)",border:`1px solid ${cfg.color}40`,borderRadius:8,color:cfg.color,padding:"5px 10px",fontSize:12,fontFamily:"Outfit,sans-serif",cursor:"pointer",fontWeight:600}}>
                        {PLANS.map(p=><option key={p} value={p}>{p}</option>)}
                      </select>
                    </td>
                    <td style={{padding:"12px 14px",textAlign:"center",fontFamily:"JetBrains Mono,monospace",color:"#00C8FF"}}>{empCnt}</td>
                    <td style={{padding:"12px 14px",textAlign:"center",fontFamily:"JetBrains Mono,monospace",color:"#7C5CFF"}}>{intCnt}</td>
                    <td style={{padding:"12px 14px",fontSize:11,color:"#7A93B4"}}>{cfg.desc}</td>
                    <td style={{padding:"12px 14px"}}>
                      <div style={{fontFamily:"JetBrains Mono,monospace",color:"#00E5A3",fontSize:15,fontWeight:800}}>{fmtRs(bill)}</div>
                    </td>
                    <td style={{padding:"12px 14px"}}>
                      <button onClick={()=>generateInvoice(c,empCnt,intCnt,c.plan)}
                        style={{display:"flex",alignItems:"center",gap:5,padding:"5px 12px",borderRadius:7,border:"1px solid rgba(0,229,163,0.2)",background:"rgba(0,229,163,0.06)",color:"#00E5A3",cursor:"pointer",fontSize:12,fontFamily:"Outfit,sans-serif",fontWeight:600}}>
                        <FileText size={11}/> Invoice
                      </button>
                    </td>
                  </tr>
                );
              })}
              <tr style={{background:"rgba(0,229,163,0.05)",borderTop:"2px solid rgba(0,229,163,0.2)"}}>
                <td colSpan={5} style={{padding:"12px 14px",fontWeight:700,color:"#E8F0FE"}}>
                  Total Monthly Revenue ({rows.filter(c=>c.status==="Active").length} active companies)
                </td>
                <td style={{padding:"12px 14px",fontFamily:"JetBrains Mono,monospace",color:"#00E5A3",fontSize:17,fontWeight:900}}>{fmtRs(totalRevenue)}</td>
                <td/>
              </tr>
            </tbody>
          </table>
        </div>
      </>)}

      {/* ═══════════ TICKETS ═══════════ */}
      {page==="tickets" && (<>
        <div style={{marginBottom:24}}>
          <div style={{fontSize:11,color:"#00C8FF",fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:4}}>SUPER ADMIN</div>
          <h2 style={{margin:0,fontSize:22,fontWeight:800}}>Support Tickets</h2>
        </div>
        <div style={{display:"flex",gap:8,marginBottom:16}}>
          {["all","Open","In Progress","Resolved","Closed"].map(s=>(
            <button key={s} onClick={()=>setTicketFilter(s)}
              style={{padding:"6px 14px",borderRadius:20,fontSize:12,cursor:"pointer",fontFamily:"Outfit,sans-serif",
                border:`1px solid ${ticketFilter===s?"rgba(0,200,255,0.3)":"rgba(255,255,255,0.06)"}`,
                background:ticketFilter===s?"rgba(0,200,255,0.08)":"rgba(255,255,255,0.02)",
                color:ticketFilter===s?"#00C8FF":"#7A93B4"}}>
              {s==="all"?"All":s}
              <span style={{marginLeft:5,fontFamily:"JetBrains Mono,monospace",fontSize:10}}>
                {s==="all"?tickets.length:tickets.filter(t=>t.status===s).length}
              </span>
            </button>
          ))}
        </div>
        <div style={card}>
          {filteredTickets.length===0?(
            <div style={{padding:40,textAlign:"center",color:"#7A93B4"}}><TicketCheck size={36} style={{color:"#3D5473",marginBottom:10}}/><div>No tickets</div></div>
          ):filteredTickets.map(t=>(
            <div key={t.id} style={{padding:"16px 20px",borderBottom:"1px solid rgba(0,200,255,0.05)"}}>
              <div style={{display:"flex",gap:12,alignItems:"flex-start"}}>
                <div style={{flex:1}}>
                  <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",marginBottom:4}}>
                    <span style={{fontSize:11,fontFamily:"JetBrains Mono,monospace",color:"#3D5473"}}>{t.ticket_no||`#${t.id}`}</span>
                    <span style={{fontSize:14,fontWeight:600}}>{t.title}</span>
                    <span style={{fontSize:11,padding:"2px 7px",borderRadius:8,background:`${P_COLOR[t.priority]||"#FFB800"}15`,color:P_COLOR[t.priority]||"#FFB800"}}>{t.priority}</span>
                    <span style={{fontSize:11,padding:"2px 7px",borderRadius:8,background:"rgba(124,92,255,0.1)",color:"#7C5CFF"}}>{t.category}</span>
                  </div>
                  <div style={{fontSize:12,color:"#7A93B4"}}>Company #{t.company_id} \u00b7 {t.created_by}</div>
                  {t.description&&<div style={{fontSize:12.5,color:"#3D5473",marginTop:5}}>{t.description}</div>}
                  {t.resolution&&<div style={{marginTop:8,padding:"8px 12px",background:"rgba(0,229,163,0.05)",border:"1px solid rgba(0,229,163,0.1)",borderRadius:8,fontSize:12.5,color:"#00E5A3"}}><strong>Resolution:</strong> {t.resolution}</div>}
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:6,alignItems:"flex-end",flexShrink:0}}>
                  <span style={{fontSize:11.5,padding:"2px 9px",borderRadius:20,fontWeight:600,background:`${S_COLOR[t.status]||"#7A93B4"}15`,color:S_COLOR[t.status]||"#7A93B4"}}>{t.status}</span>
                  {t.status!=="Closed"&&(
                    <button onClick={()=>{setViewTicket(t);setReplyText(t.resolution||"");}}
                      style={{padding:"4px 10px",borderRadius:6,border:"1px solid rgba(0,200,255,0.2)",background:"none",color:"#00C8FF",cursor:"pointer",fontSize:12,fontFamily:"Outfit,sans-serif"}}>
                      Respond
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </>)}

      {/* ═══════════ ANALYTICS ═══════════ */}
      {page==="analytics" && (<>
        <div style={{marginBottom:24}}>
          <div style={{fontSize:11,color:"#00C8FF",fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:4}}>SUPER ADMIN</div>
          <h2 style={{margin:0,fontSize:22,fontWeight:800}}>Platform Analytics</h2>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14}}>
          {[
            {label:"Total Companies",   val:companies.length,                                   color:"#00C8FF"},
            {label:"Active Companies",  val:companies.filter(c=>c.status==="Active").length,    color:"#00E5A3"},
            {label:"Total Employees",   val:companies.reduce((a,c)=>a+(c.employee_count||0),0), color:"#7C5CFF"},
            {label:"Monthly Revenue",   val:fmtRs(totalRevenue),                                color:"#FFB800"},
            {label:"Open Tickets",      val:tickets.filter(t=>t.status==="Open").length,        color:"#FF4D6D"},
            {label:"Resolved Tickets",  val:tickets.filter(t=>["Resolved","Closed"].includes(t.status)).length, color:"#00E5A3"},
          ].map(({label,val,color})=>(
            <div key={label} style={{...card,padding:20,borderTop:`3px solid ${color}`}}>
              <div style={{fontSize:28,fontWeight:900,fontFamily:"JetBrains Mono,monospace",color}}>{val}</div>
              <div style={{fontSize:12,color:"#7A93B4",marginTop:6,textTransform:"uppercase",letterSpacing:"0.07em"}}>{label}</div>
            </div>
          ))}
        </div>
      </>)}

      {/* ═══════════ SETTINGS ═══════════ */}
      {page==="settings" && (<>
        <div style={{marginBottom:24}}>
          <div style={{fontSize:11,color:"#00C8FF",fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:4}}>SUPER ADMIN</div>
          <h2 style={{margin:0,fontSize:22,fontWeight:800}}>Platform Settings</h2>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
          <div style={{...card,padding:24}}>
            <div style={{fontSize:14,fontWeight:700,color:"#E8F0FE",marginBottom:16}}>Super Admin Credentials</div>
            <div style={{marginBottom:12}}>
              <label style={lbl}>Login Email</label>
              <input value="superadmin@platform.com" readOnly style={{...inp(),opacity:0.7}}/>
            </div>
            <div style={{background:"rgba(255,184,0,0.06)",border:"1px solid rgba(255,184,0,0.15)",borderRadius:8,padding:"10px 14px",fontSize:12.5,color:"#FFB800"}}>
              \u26a0\ufe0f Password set in <code style={{color:"#00C8FF"}}>auth_enhanced.py</code> \u2192 <code>SUPER_ADMIN_PASSWORD</code>
            </div>
          </div>
          <div style={{...card,padding:24}}>
            <div style={{fontSize:14,fontWeight:700,color:"#E8F0FE",marginBottom:16}}>Pricing Model</div>
            {[
              ["Basic HRMS",           "\u20b94,500/mo + \u20b945/emp"],
              ["Advanced HRMS",        "\u20b96,500/mo + \u20b965/emp (incl. Time Tracking)"],
              ["AI Interview",         "\u20b93,500/mo + \u20b935/interview"],
              ["Advanced HRMS + AI",   "\u20b99,000/mo + \u20b980/emp (all features)"],
              ["Enterprise",           "Custom pricing"],
            ].map(([k,v])=>(
              <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid rgba(0,200,255,0.06)",fontSize:13}}>
                <span style={{color:"#7A93B4"}}>{k}</span>
                <span style={{color:"#E8F0FE",fontFamily:"JetBrains Mono,monospace",fontSize:12}}>{v}</span>
              </div>
            ))}
          </div>
          <div style={{...card,padding:24}}>
            <div style={{fontSize:14,fontWeight:700,color:"#E8F0FE",marginBottom:16}}>App Configuration</div>
            {[["App Name","Hirality HRMS"],["DB","PostgreSQL \u2014 hrms_db"],["SMTP","smtp.hostinger.com:465"]].map(([k,v])=>(
              <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"7px 0",borderBottom:"1px solid rgba(0,200,255,0.06)",fontSize:13}}>
                <span style={{color:"#7A93B4"}}>{k}</span><span style={{color:"#E8F0FE",fontFamily:"JetBrains Mono,monospace",fontSize:12}}>{v}</span>
              </div>
            ))}
          </div>
          <div style={{...card,padding:24}}>
            <div style={{fontSize:14,fontWeight:700,color:"#E8F0FE",marginBottom:16}}>Quick Links</div>
            {[["API Docs","http://localhost:8000/docs","#00C8FF"],["Swagger UI","http://localhost:8000/redoc","#7C5CFF"]].map(([l,u,c])=>(
              <a key={l} href={u} target="_blank" rel="noreferrer"
                style={{display:"block",padding:"9px 14px",borderRadius:8,border:`1px solid ${c}20`,background:`${c}08`,color:c,fontSize:13,fontWeight:600,marginBottom:8,textDecoration:"none"}}>
                {l} \u2192
              </a>
            ))}
          </div>
        </div>
      </>)}

      {/* ═══════════ MODALS ═══════════ */}

      {viewCo && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:9998,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setViewCo(null)}>
          <div style={{background:"#0D1B2E",border:"1px solid rgba(0,200,255,0.2)",borderRadius:14,width:520,overflow:"hidden",fontFamily:"Outfit,sans-serif"}} onClick={e=>e.stopPropagation()}>
            <div style={{padding:"18px 24px",borderBottom:"1px solid rgba(0,200,255,0.08)",display:"flex",justifyContent:"space-between"}}>
              <h3 style={{margin:0,fontSize:16,fontWeight:600}}>{viewCo.name}</h3>
              <button onClick={()=>setViewCo(null)} style={{background:"none",border:"none",cursor:"pointer",color:"#7A93B4"}}><X size={16}/></button>
            </div>
            <div style={{padding:24}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
                {[["Company",viewCo.name],["Domain",viewCo.domain||"\u2014"],["Admin Email",viewCo.admin_email],
                  ["Plan",viewCo.plan||"Basic HRMS"],["Status",viewCo.status||"Active"],
                  ["Employees",viewCo.employee_count||0],
                  ["Monthly Bill",fmtRs(calcBill(viewCo.plan||"Basic HRMS",viewCo.employee_count||0,0))],
                ].map(([k,v])=>(
                  <div key={k} style={{background:"rgba(255,255,255,0.02)",borderRadius:8,padding:"10px 14px"}}>
                    <div style={{fontSize:10.5,color:"#7A93B4",textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3}}>{k}</div>
                    <div style={{fontSize:13,color:"#E8F0FE",fontWeight:500}}>{String(v)}</div>
                  </div>
                ))}
              </div>
              <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
                <button onClick={()=>generateInvoice(viewCo,viewCo.employee_count||0,0,viewCo.plan||"Basic HRMS")}
                  style={{padding:"8px 16px",borderRadius:8,border:"1px solid rgba(0,229,163,0.3)",background:"rgba(0,229,163,0.06)",color:"#00E5A3",cursor:"pointer",fontFamily:"Outfit,sans-serif",fontSize:13,display:"flex",alignItems:"center",gap:6}}>
                  <FileText size={13}/> Invoice
                </button>
                <button onClick={()=>{setViewCo(null);resendInvite(viewCo.id);}}
                  style={{padding:"8px 16px",borderRadius:8,border:"1px solid rgba(255,184,0,0.3)",background:"rgba(255,184,0,0.08)",color:"#FFB800",cursor:"pointer",fontFamily:"Outfit,sans-serif",fontSize:13,display:"flex",alignItems:"center",gap:6}}>
                  <Mail size={13}/> Resend Invite
                </button>
                <button onClick={()=>{setViewCo(null);setEditCo({...viewCo});}}
                  style={{padding:"8px 16px",borderRadius:8,border:"none",background:"linear-gradient(135deg,#00C8FF,#7C5CFF)",color:"#fff",cursor:"pointer",fontFamily:"Outfit,sans-serif",fontSize:13,display:"flex",alignItems:"center",gap:6}}>
                  <Edit2 size={13}/> Edit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editCo && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:9998,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setEditCo(null)}>
          <div style={{background:"#0D1B2E",border:"1px solid rgba(0,200,255,0.2)",borderRadius:14,width:480,overflow:"hidden",fontFamily:"Outfit,sans-serif"}} onClick={e=>e.stopPropagation()}>
            <div style={{padding:"18px 24px",borderBottom:"1px solid rgba(0,200,255,0.08)",display:"flex",justifyContent:"space-between"}}>
              <h3 style={{margin:0,fontSize:15,fontWeight:600}}>Edit \u2014 {editCo.name}</h3>
              <button onClick={()=>setEditCo(null)} style={{background:"none",border:"none",cursor:"pointer",color:"#7A93B4"}}><X size={15}/></button>
            </div>
            <div style={{padding:22,display:"flex",flexDirection:"column",gap:12}}>
              <div><label style={lbl}>Company Name</label><input value={editCo.name||""} onChange={e=>setEditCo({...editCo,name:e.target.value})} style={inp()}/></div>
              <div><label style={lbl}>Domain</label><input value={editCo.domain||""} onChange={e=>setEditCo({...editCo,domain:e.target.value})} style={inp()}/></div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                <div><label style={lbl}>Plan</label>
                  <select value={editCo.plan||"Basic HRMS"} onChange={e=>setEditCo({...editCo,plan:e.target.value})} style={inp()}>
                    {PLANS.map(p=><option key={p}>{p}</option>)}
                  </select>
                </div>
                <div><label style={lbl}>Status</label>
                  <select value={editCo.status||"Active"} onChange={e=>setEditCo({...editCo,status:e.target.value})} style={inp()}>
                    {["Active","Inactive","Trial"].map(s=><option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div><label style={lbl}>Notes</label><textarea value={editCo.notes||""} onChange={e=>setEditCo({...editCo,notes:e.target.value})} rows={2} style={{...inp(),resize:"vertical"}}/></div>
              <div style={{display:"flex",gap:10,justifyContent:"flex-end",paddingTop:8,borderTop:"1px solid rgba(0,200,255,0.08)"}}>
                <button onClick={()=>setEditCo(null)} style={{padding:"9px 18px",borderRadius:8,border:"1px solid rgba(255,255,255,0.08)",background:"none",color:"#7A93B4",cursor:"pointer",fontFamily:"Outfit,sans-serif",fontSize:13}}>Cancel</button>
                <button onClick={updateCompany} style={{padding:"9px 20px",borderRadius:8,border:"none",background:"linear-gradient(135deg,#00C8FF,#7C5CFF)",color:"#fff",cursor:"pointer",fontFamily:"Outfit,sans-serif",fontSize:13,fontWeight:700}}>Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {createCo && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:9998,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setCreateCo(false)}>
          <div style={{background:"#0D1B2E",border:"1px solid rgba(0,200,255,0.2)",borderRadius:14,width:500,overflow:"hidden",fontFamily:"Outfit,sans-serif"}} onClick={e=>e.stopPropagation()}>
            <div style={{padding:"18px 24px",borderBottom:"1px solid rgba(0,200,255,0.08)",display:"flex",justifyContent:"space-between"}}>
              <h3 style={{margin:0,fontSize:15,fontWeight:600}}>Add New Company</h3>
              <button onClick={()=>setCreateCo(false)} style={{background:"none",border:"none",cursor:"pointer",color:"#7A93B4"}}><X size={15}/></button>
            </div>
            <div style={{padding:22,display:"flex",flexDirection:"column",gap:12}}>
              <div><label style={lbl}>Company Name *</label><input value={coForm.name} onChange={e=>setCoForm({...coForm,name:e.target.value})} placeholder="e.g. Acme Corp" style={inp(!coForm.name)}/></div>
              <div><label style={lbl}>Domain</label><input value={coForm.domain} onChange={e=>setCoForm({...coForm,domain:e.target.value})} placeholder="acmecorp.com" style={inp()}/></div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                <div><label style={lbl}>Admin Name</label><input value={coForm.admin_name} onChange={e=>setCoForm({...coForm,admin_name:e.target.value})} placeholder="Full name" style={inp()}/></div>
                <div><label style={lbl}>Admin Email *</label><input value={coForm.admin_email} onChange={e=>setCoForm({...coForm,admin_email:e.target.value})} placeholder="admin@company.com" style={inp(!coForm.admin_email)}/></div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                <div><label style={lbl}>Plan</label>
                  <select value={coForm.plan} onChange={e=>setCoForm({...coForm,plan:e.target.value})} style={inp()}>
                    {PLANS.map(p=><option key={p}>{p}</option>)}
                  </select>
                </div>
                <div><label style={lbl}>Max Employees</label><input type="number" value={coForm.max_employees} onChange={e=>setCoForm({...coForm,max_employees:parseInt(e.target.value)||50})} style={inp()}/></div>
              </div>
              <div><label style={lbl}>Notes</label><textarea value={coForm.notes} onChange={e=>setCoForm({...coForm,notes:e.target.value})} rows={2} style={{...inp(),resize:"vertical"}}/></div>
              <div>
                <label style={lbl}>Modules</label>
                <div style={{display:"flex",gap:8}}>
                  {[
                    {v:"hrms", label:"HRMS Only",  color:"#7C5CFF"},
                    {v:"ats",  label:"ATS Only",   color:"#00C8FF"},
                    {v:"both", label:"HRMS & ATS", color:"#00E5A3"},
                  ].map(opt=>(
                    <label key={opt.v} style={{
                      flex:1, display:"flex", alignItems:"center", justifyContent:"center", gap:6,
                      padding:"9px 10px", borderRadius:8, cursor:"pointer", fontSize:12.5, fontWeight:600,
                      border:`1px solid ${coForm.modules===opt.v ? opt.color : "rgba(255,255,255,0.08)"}`,
                      background: coForm.modules===opt.v ? `${opt.color}18` : "rgba(255,255,255,0.02)",
                      color: coForm.modules===opt.v ? opt.color : "#7A93B4",
                    }}>
                      <input type="radio" name="modules" value={opt.v}
                        checked={coForm.modules===opt.v}
                        onChange={e=>setCoForm({...coForm,modules:e.target.value})}
                        style={{display:"none"}}/>
                      {opt.label}
                    </label>
                  ))}
                </div>
                <div style={{fontSize:11,color:"#3D5473",marginTop:5}}>
                  {coForm.modules==="hrms" && "This company will only be able to access HRMS — no ticketing."}
                  {coForm.modules==="ats"  && "This company will only be able to access ATS — no HRMS (payroll, leave, attendance, etc.)."}
                  {coForm.modules==="both" && "This company gets both HRMS and ATS."}
                </div>
              </div>
              <div style={{background:"rgba(0,200,255,0.04)",border:"1px solid rgba(0,200,255,0.08)",borderRadius:8,padding:"9px 14px",fontSize:12.5,color:"#7A93B4"}}>
                \U0001f4e7 Invite will be sent to <strong style={{color:"#00C8FF"}}>{coForm.admin_email||"the admin email"}</strong>
              </div>
              <div style={{display:"flex",gap:10,justifyContent:"flex-end",paddingTop:8,borderTop:"1px solid rgba(0,200,255,0.08)"}}>
                <button onClick={()=>setCreateCo(false)} style={{padding:"9px 18px",borderRadius:8,border:"1px solid rgba(255,255,255,0.08)",background:"none",color:"#7A93B4",cursor:"pointer",fontFamily:"Outfit,sans-serif",fontSize:13}}>Cancel</button>
                <button onClick={createCompany} style={{padding:"9px 20px",borderRadius:8,border:"none",background:"linear-gradient(135deg,#00C8FF,#7C5CFF)",color:"#fff",cursor:"pointer",fontFamily:"Outfit,sans-serif",fontSize:13,fontWeight:700}}>Create & Send Invite</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {delCo && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:9998,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setDelCo(null)}>
          <div style={{background:"#0D1B2E",border:"1px solid rgba(255,184,0,0.3)",borderRadius:14,width:420,padding:28,fontFamily:"Outfit,sans-serif"}} onClick={e=>e.stopPropagation()}>
            <h3 style={{margin:"0 0 12px",color:"#FFB800"}}>Mark as Deleted?</h3>
            <p style={{color:"#7A93B4",fontSize:13,margin:"0 0 12px"}}>
              <strong style={{color:"#E8F0FE"}}>{delCo.name}</strong> will be marked as <strong style={{color:"#FF4D6D"}}>Deleted</strong>.
            </p>
            <div style={{background:"rgba(255,184,0,0.06)",border:"1px solid rgba(255,184,0,0.15)",borderRadius:8,padding:"10px 14px",fontSize:12.5,color:"#FFB800",marginBottom:20}}>
              ℹ️ All users will be deactivated and cannot login.<br/>
              Data is <strong>preserved</strong> — company can be reactivated later.<br/>
              To permanently remove all data, use <strong>Purge</strong> after deletion.
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <button onClick={()=>setDelCo(null)} style={{padding:"9px 18px",borderRadius:8,border:"1px solid rgba(255,255,255,0.08)",background:"none",color:"#7A93B4",cursor:"pointer",fontFamily:"Outfit,sans-serif",fontSize:13}}>Cancel</button>
              <button onClick={()=>deleteCompany(delCo.id)} style={{padding:"9px 18px",borderRadius:8,border:"none",background:"#FFB800",color:"#06101F",cursor:"pointer",fontFamily:"Outfit,sans-serif",fontSize:13,fontWeight:700}}>Mark as Deleted</button>
            </div>
          </div>
        </div>
      )}

      {purgeCo && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setPurgeCo(null)}>
          <div style={{background:"#0D1B2E",border:"1px solid rgba(255,77,109,0.5)",borderRadius:14,width:420,padding:28,fontFamily:"Outfit,sans-serif"}} onClick={e=>e.stopPropagation()}>
            <h3 style={{margin:"0 0 12px",color:"#FF4D6D"}}>☠ Permanently Delete?</h3>
            <p style={{color:"#7A93B4",fontSize:13,margin:"0 0 12px"}}>
              This will permanently remove <strong style={{color:"#E8F0FE"}}>{purgeCo.name}</strong> and <strong style={{color:"#FF4D6D"}}>ALL data</strong>.
            </p>
            <div style={{background:"rgba(255,77,109,0.06)",border:"1px solid rgba(255,77,109,0.2)",borderRadius:8,padding:"10px 14px",fontSize:12.5,color:"#FF4D6D",marginBottom:20}}>
              ⚠️ This will delete ALL employees, payroll, attendance, platform users and company data.<br/>
              <strong>This cannot be undone.</strong>
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
              <button onClick={()=>setPurgeCo(null)} style={{padding:"9px 18px",borderRadius:8,border:"1px solid rgba(255,255,255,0.08)",background:"none",color:"#7A93B4",cursor:"pointer",fontFamily:"Outfit,sans-serif",fontSize:13}}>Cancel</button>
              <button onClick={()=>permanentDelete(purgeCo.id)} style={{padding:"9px 18px",borderRadius:8,border:"none",background:"#FF4D6D",color:"#fff",cursor:"pointer",fontFamily:"Outfit,sans-serif",fontSize:13,fontWeight:700}}>☠ Permanently Delete</button>
            </div>
          </div>
        </div>
      )}

      {migrateCo && (
        <CompanyMigrationModal
          company={migrateCo}
          onClose={() => setMigrateCo(null)}
          onSuccess={() => { flash(`✅ Migration completed for ${migrateCo.name}`); load(); }}
        />
      )}

      {viewTicket && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:9998,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setViewTicket(null)}>
          <div style={{background:"#0D1B2E",border:"1px solid rgba(0,200,255,0.2)",borderRadius:14,width:520,overflow:"hidden",fontFamily:"Outfit,sans-serif"}} onClick={e=>e.stopPropagation()}>
            <div style={{padding:"18px 24px",borderBottom:"1px solid rgba(0,200,255,0.08)",display:"flex",justifyContent:"space-between"}}>
              <div>
                <div style={{fontSize:11,color:"#7A93B4",marginBottom:3}}>{viewTicket.ticket_no||`#${viewTicket.id}`}</div>
                <h3 style={{margin:0,fontSize:15,fontWeight:600}}>{viewTicket.title}</h3>
              </div>
              <button onClick={()=>setViewTicket(null)} style={{background:"none",border:"none",cursor:"pointer",color:"#7A93B4"}}><X size={15}/></button>
            </div>
            <div style={{padding:22}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
                {[["Company",`#${viewTicket.company_id}`],["Raised by",viewTicket.created_by],["Priority",viewTicket.priority],["Category",viewTicket.category]].map(([k,v])=>(
                  <div key={k} style={{background:"rgba(255,255,255,0.02)",borderRadius:8,padding:"9px 12px"}}>
                    <div style={{fontSize:10.5,color:"#7A93B4",textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:3}}>{k}</div>
                    <div style={{fontSize:13,color:"#E8F0FE"}}>{v}</div>
                  </div>
                ))}
              </div>
              {viewTicket.description&&<div style={{padding:"10px 14px",background:"rgba(255,255,255,0.02)",borderRadius:8,marginBottom:14,fontSize:13,color:"#7A93B4",lineHeight:1.6}}>{viewTicket.description}</div>}
              <label style={lbl}>Resolution / Response</label>
              <textarea value={replyText} onChange={e=>setReplyText(e.target.value)} rows={3}
                placeholder="Describe the resolution\u2026" style={{...inp(),resize:"vertical",marginBottom:12}}/>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>updateTicket(viewTicket.id,"In Progress",replyText)}
                  style={{flex:1,padding:"9px",borderRadius:8,border:"1px solid rgba(0,200,255,0.3)",background:"rgba(0,200,255,0.08)",color:"#00C8FF",cursor:"pointer",fontFamily:"Outfit,sans-serif",fontSize:13,fontWeight:600}}>
                  Mark In Progress
                </button>
                <button onClick={()=>updateTicket(viewTicket.id,"Resolved",replyText)}
                  style={{flex:1,padding:"9px",borderRadius:8,border:"none",background:"linear-gradient(135deg,#00E5A3,#00C8FF)",color:"#06101F",cursor:"pointer",fontFamily:"Outfit,sans-serif",fontSize:13,fontWeight:700}}>
                  \u2713 Resolved
                </button>
                <button onClick={()=>updateTicket(viewTicket.id,"Closed",replyText)}
                  style={{padding:"9px 14px",borderRadius:8,border:"1px solid rgba(255,255,255,0.08)",background:"none",color:"#7A93B4",cursor:"pointer",fontFamily:"Outfit,sans-serif",fontSize:13}}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {toast&&(
        <div style={{position:"fixed",bottom:28,left:"50%",transform:"translateX(-50%)",padding:"10px 22px",borderRadius:10,fontSize:13,fontWeight:600,zIndex:9999,
          background:"#0D1B2E",border:`1px solid ${toast.startsWith("\u2705")?"rgba(0,229,163,0.3)":"rgba(255,77,109,0.3)"}`,
          color:toast.startsWith("\u2705")?"#00E5A3":"#FF4D6D",boxShadow:"0 8px 32px rgba(0,0,0,0.5)",fontFamily:"Outfit,sans-serif"}}>
          {toast}
        </div>
      )}
    </div>
  );
}